/**
 * App.js
 * 
 * Этот файл является точкой входа для приложения TPH Truck Parking.
 * Он отвечает за настройку маршрутов с использованием React Router и предоставление контекста для всего приложения.
 * 
 * Основные функции:
 * - Определение маршрутов для различных страниц приложения (главная, список локаций, карточки цен и т.д.).
 * - Предоставление контекста через AppProvider для управления глобальным состоянием.
 * - Отображение основных компонентов, таких как Header и Footer.
 */
import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
  useLocation,
} from 'react-router-dom';
import Home from './pages/Home';
import NavBar from './components/NavBar';
import SiteBrandingHead from './components/SiteBrandingHead';
import YardFooter from './components/yard/YardFooter';
import FloatingManageBooking from './components/yard/FloatingManageBooking';
import { AppProvider } from './context/AppContext';
import { BookingSessionProvider } from './context/BookingSessionContext';
import { TenantIdentityProvider } from './context/TenantIdentityContext';
import './App.css';
import { Toaster, ToastBar, prewarmHotToast } from 'react-hot-toast';
import LoadingSpinner from './components/common/LoadingSpinner';
import { BackgroundLayout } from './components/background';
import { ThemeProvider } from './context/ThemeContext';
import MaintenanceWrapper from './components/MaintenanceWrapper';
import RouteScrollManager from './components/routing/RouteScrollManager';
import RouteSeoPolicy from './components/routing/RouteSeoPolicy';
import { getSentryRoutesComponent } from './monitoring/sentry';
import { useActiveHoliday } from './hooks/useActiveHoliday';
import { isToastRuntimeActivated, subscribeToastRuntime } from './utils/toastRuntime';
import { shouldEnableAuthRuntime } from './utils/authRuntime';
import { buildLegacyTruckParkingRedirectPath } from './utils/truckParkingDiscovery';
import { shouldShowPublicChrome, shouldUseDarkOverscroll } from './utils/publicChromeVisibility';
import { featureFlags } from './config/featureFlags';
import { tenantLegacyAccountDestination } from './config/tenantWorkspace';
// Lazy-load non-critical route/page code to reduce initial JS payload.
const Auth0ProviderWithHistory = lazy(() => import('./auth/Auth0Provider'));
const AuthSiteSettingsProvider = lazy(() => import('./context/AuthSiteSettingsProvider'));
const SessionMonitor = lazy(() => import('./components/auth/SessionMonitor'));
const ProtectedRoute = lazy(() => import('./auth/ProtectedRoute'));
const TenantProtectedRoute = lazy(() => import('./auth/TenantProtectedRoute'));
const BookingDetails = lazy(() => import('./pages/BookingDetails'));
const SignInPage = lazy(() => import('./pages/SignInPage'));
const OwnerAccessPage = lazy(() => import('./pages/OwnerAccessPage'));
const Auth0CallbackHandler = lazy(() => import('./components/auth/Auth0CallbackHandler'));
const Snowfall = lazy(() => import('./components/Snowfall'));
// Lazy load the QRCheckoutPage
const QRCheckoutPage = lazy(() => import('./pages/QRCheckoutPage'));
// Lazy load PM Partner detail page
const PMPartnersPage = lazy(() => import('./pages/PMPartnersPage'));
const PartnerPage = lazy(() => import('./pages/PartnerPage'));
// Lazy load repair intent pages
const RepairIntentPage = lazy(() => import('./pages/RepairIntentPage'));
const LastHourParkingPage = lazy(() => import('./pages/LastHourParkingPage'));
const HighwayCorridorPage = lazy(() => import('./pages/HighwayCorridorPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const HomeOrLandingGate = lazy(() => import('./pages/HomeOrLandingGate'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const LandlordLandingPage = lazy(() => import('./pages/LandlordLandingPage'));
const FoundingOfferPage = lazy(() => import('./pages/FoundingOfferPage'));
// Lazy load the ManageBookingPage
const ManageBookingPage = lazy(() => import('./pages/ManageBookingPage'));
// Lazy load the AdminDashboard
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const HiringOperationsPage = lazy(() => import('./pages/HiringOperationsPage'));
// Lazy load the NewHirePage
const NewHirePage = lazy(() => import('./pages/NewHirePage'));
const WeHirePage = lazy(() => import('./pages/WeHirePage'));
const HiringApplyRedirectPage = lazy(() => import('./pages/HiringApplyRedirectPage'));
// Lazy load the ErrorAlertPage
const ErrorAlertPage = lazy(() => import('./pages/ErrorAlertPage'));
// Lazy load the ErrorAlertsPage (list of all errors)
const ErrorAlertsPage = lazy(() => import('./pages/ErrorAlertsPage'));
const ReportPage = lazy(() => import('./pages/ReportPage'));
const AppSettingsPage = lazy(() => import('./pages/AppSettingsPage.tsx'));
const ManagerConsolePage = lazy(() => import('./pages/ManagerConsolePage'));
const ManagerSettingsPage = lazy(() => import('./pages/ManagerSettingsPage'));
const PartnersAdminPage = lazy(() => import('./pages/PartnersAdminPage'));
const LandlordsAdminPage = lazy(() => import('./pages/LandlordsAdminPage'));
const TenantOperationsPage = lazy(() => import('./pages/TenantOperationsPage'));
const StripeAdminPage = lazy(() => import('./pages/StripeAdminPage'));
// Lazy load the LinkExpiredPage
const LinkExpiredPage = lazy(() => import('./pages/LinkExpiredPage'));
// Lazy load Magic Link and Booking Dashboard pages
const MagicLinkAuthPage = lazy(() => import('./pages/MagicLinkAuthPage'));
const BookingDashboardPage = lazy(() => import('./pages/BookingDashboardPage'));
// Lazy load Extension Magic Link page
const ExtendBookingPage = lazy(() => import('./pages/ExtendBookingPage'));
const InstrumentedRoutes = getSentryRoutesComponent(Routes);

const TenantLegacyAccountRedirect = () => {
  const location = useLocation();
  return <Navigate to={tenantLegacyAccountDestination(location.search)} replace />;
};

const TOAST_PREWARM_ROUTE_PATTERNS = [
  /^\/checkout(?:\/|$)/i,
  /^\/manage-booking(?:\/|$)/i,
  /^\/booking-details(?:\/|$)/i,
  /^\/extend(?:\/|$)/i,
  /^\/my-bookings(?:\/|$)/i,
];

const shouldPrewarmToastRuntime = (pathname = '') => (
  TOAST_PREWARM_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname))
);

const CustomerProfileHiringRoute = ({ children }) => {
  const location = useLocation();
  const allowCustomerProfileAccess = Boolean(location?.state?.allowCustomerHiringAccess);
  if (!featureFlags.publicHiringEnabled && !allowCustomerProfileAccess) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const RouteAnalyticsTracker = () => {
  const location = useLocation();
  const lastTrackedPathRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    const path = `${location.pathname}${location.search || ''}`;
    if (lastTrackedPathRef.current === path) return;
    lastTrackedPathRef.current = path;

    const track = async () => {
      try {
        const { trackPageView } = await import('./utils/analytics');
        if (!cancelled) {
          trackPageView({ path });
        }
      } catch (_error) {
        // no-op
      }
    };

    track();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search]);

  return null;
};

const LegacyLocationsRedirect = () => {
  const location = useLocation();
  const redirectPath = buildLegacyTruckParkingRedirectPath({
    pathname: location.pathname,
    search: location.search,
  });

  return <Navigate to={redirectPath || '/truck-parking'} replace />;
};

const AppContent = () => {
  const location = useLocation();
  const enableAuthRuntime = shouldEnableAuthRuntime(location.pathname);
  const [shouldRenderToaster, setShouldRenderToaster] = useState(() => isToastRuntimeActivated());
  const activeHoliday = useActiveHoliday();
  const isDecember = new Date().getMonth() === 11;
  const shouldRenderSnowfall = process.env.REACT_APP_CHRISTMAS_THEME === 'true'
    || (process.env.REACT_APP_CHRISTMAS_THEME !== 'false' && isDecember);
  const shouldLoadHolidayTheme = shouldRenderSnowfall || Boolean(activeHoliday);
  const showPublicChrome = shouldShowPublicChrome(location.pathname);

  useEffect(() => {
    if (!shouldLoadHolidayTheme) return;
    import('./styles/ChristmasTheme.css').catch(() => {
      // no-op: seasonal styling should never block app render
    });
  }, [shouldLoadHolidayTheme]);

  useEffect(() => {
    const unsubscribe = subscribeToastRuntime(() => {
      setShouldRenderToaster(true);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!shouldPrewarmToastRuntime(location.pathname)) return;
    if (typeof prewarmHotToast === 'function') {
      prewarmHotToast();
    }
  }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('yard-overscroll-dark', shouldUseDarkOverscroll(location.pathname));
    return () => {
      root.classList.remove('yard-overscroll-dark');
    };
  }, [location.pathname]);

  const appFrame = (
    <>
      {enableAuthRuntime && (
        <Suspense fallback={null}>
          <SessionMonitor />
        </Suspense>
      )}
      {shouldRenderSnowfall && (
        <Suspense fallback={null}>
          <Snowfall />
        </Suspense>
      )}
      <AppProvider>
        <ThemeProvider>
          <SiteBrandingHead />
          <MaintenanceWrapper>
            <BackgroundLayout>
              {shouldRenderToaster && (
                <Toaster
                  position="top-right"
                  reverseOrder={false}
                  gutter={8}
                  containerStyle={{}}
                  toastOptions={{
                    className: '',
                    duration: 5000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                      maxWidth: '93vw',
                      fontSize: 'clamp(12px, 3vw, 16px)',
                    },
                    success: {
                      duration: 3000,
                      theme: {
                        primary: 'green',
                        secondary: 'black',
                      },
                    },
                    error: {
                      duration: 6000,
                    },
                  }}
                >
                  {(t) => (
                    <div
                      style={{
                        animation: t.visible
                          ? 'toast-enter-right 0.35s cubic-bezier(.21,1.02,.73,1) forwards'
                          : 'toast-exit-right 0.3s cubic-bezier(.06,.71,.55,1) forwards',
                      }}
                    >
                      <ToastBar toast={t} />
                    </div>
                  )}
                </Toaster>
              )}
              <div className="app-wrapper">
                <NavBar showUserProfile />
                <main className="main-content pt-[60px] min-[900px]:pt-[72px]">
                  <Suspense fallback={<LoadingSpinner message="Loading application..." />}>
                    <InstrumentedRoutes>
                    {/* Public Routes */}
                    <Route path="/" element={<HomeOrLandingGate />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogPage />} />
                    <Route path="/truck-parking" element={<Home />} />
                    <Route path="/truck-parking/:stateSlug" element={<Home />} />
                    <Route path="/truck-parking/:stateSlug/:citySlug" element={<Home />} />
                    <Route path="/truck-parking/:stateSlug/:citySlug/:intentSlug" element={<Home />} />
                    <Route path="/list-your-lot" element={<LandlordLandingPage />} />
                    <Route path="/request-parking" element={<LandlordLandingPage />} />
                    <Route path="/founding-offer/:token" element={<FoundingOfferPage />} />
                    <Route path="/locations" element={<LegacyLocationsRedirect />} />
                    <Route path="/locations/:stateSlug" element={<LegacyLocationsRedirect />} />
                    <Route path="/rates" element={<Home />} />
                    <Route path="/faq" element={<Home />} />
                    <Route path="/about" element={<Home />} />
                    <Route path="/pm-partners" element={<PMPartnersPage />} />
                    <Route path="/truck-repair-and-maintenance" element={<RepairIntentPage />} />
                    <Route path="/truck-repair-and-maintenance/preventive-maintenance" element={<RepairIntentPage />} />
                    <Route path="/truck-repair-and-maintenance/truck-breakdown-support" element={<RepairIntentPage />} />
                    <Route path="/truck-repair-and-maintenance/garage-platform-management" element={<RepairIntentPage />} />
                    <Route path="/truck-repair-and-maintenance/dispatch-garage-coordination" element={<RepairIntentPage />} />
                    <Route path="/truck-parking/last-hour-parking" element={<LastHourParkingPage />} />
                    <Route path="/truck-parking/near-i-77" element={<HighwayCorridorPage />} />
                    <Route path="/truck-parking/near-i-85" element={<HighwayCorridorPage />} />
                    <Route path="/truck-parking/near-i-485" element={<HighwayCorridorPage />} />
                    <Route path="/truck-parking/near-us-29" element={<HighwayCorridorPage />} />
                    <Route path="/truck-parking/near-i-71" element={<HighwayCorridorPage />} />
                    <Route path="/truck-parking/near-i-279" element={<HighwayCorridorPage />} />
                    <Route path="/truck-parking/near-i-75-georgia" element={<HighwayCorridorPage />} />
                    <Route path="/truck-parking/near-i-285" element={<HighwayCorridorPage />} />
                    <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/pricing/:stateCode/:citySlug/:streetLocationToken" element={<Home />} />
                    <Route path="/pricing/:locationId" element={<Home />} />
                    <Route path="/signin" element={<SignInPage />} />
                    <Route path="/owner-access" element={<TenantIdentityProvider allowLegacyAuth0={false}><OwnerAccessPage /></TenantIdentityProvider>} />
                    <Route path="/login" element={<Navigate to="/signin" replace />} />
                    {/* Auth0 Callback Route */}
                    <Route path="/callback" element={<Auth0CallbackHandler />} />
                    <Route path="/checkout" element={<QRCheckoutPage />} />
                    {/* PM Partner detail page */}
                    <Route path="/pm-partners/:slug" element={<PartnerPage />} />
                    {/* /hiring/apply is a transactional candidate-onboarding redirect linked from email — it must render regardless of the public hiring marketplace feature flag. */}
                    <Route path="/hiring/apply" element={<HiringApplyRedirectPage />} />
                    <Route path="/hiring" element={<CustomerProfileHiringRoute><WeHirePage /></CustomerProfileHiringRoute>} />
                    <Route path="/hiring/26ft" element={<CustomerProfileHiringRoute><Navigate to="/hiring/box-truck" replace /></CustomerProfileHiringRoute>} />
                    <Route path="/hiring/otr" element={<CustomerProfileHiringRoute><NewHirePage /></CustomerProfileHiringRoute>} />
                    <Route path="/hiring/oversize" element={<CustomerProfileHiringRoute><NewHirePage /></CustomerProfileHiringRoute>} />
                    <Route path="/hiring/box-truck" element={<CustomerProfileHiringRoute><NewHirePage /></CustomerProfileHiringRoute>} />
                    <Route path="/hiring/amazon-relay" element={<CustomerProfileHiringRoute><NewHirePage /></CustomerProfileHiringRoute>} />
                    <Route path="/manage-booking" element={<ManageBookingPage />} />
                    <Route path="/booking-details/:bookingId" element={<BookingDetails />} />
                    {/* Link Expired Page */}
                    <Route path="/link-expired" element={<LinkExpiredPage />} />
                    {/* Magic Link Authentication Routes */}
                    <Route path="/auth/:code" element={<MagicLinkAuthPage />} />
                    <Route path="/m/:code" element={<MagicLinkAuthPage />} />
                    {/* Extension Magic Link Route */}
                    <Route path="/extend/:code" element={<ExtendBookingPage />} />
                    {/* Booking Dashboard (Full History) */}
                    <Route path="/my-bookings" element={<BookingDashboardPage />} />
                    <Route path="/tenant/account" element={<TenantProtectedRoute><TenantLegacyAccountRedirect /></TenantProtectedRoute>} />
                    <Route path="/tenant/*" element={<TenantProtectedRoute><TenantOperationsPage /></TenantProtectedRoute>} />
                    {/* Spot Changes Log Page (Client Access) */}
                <Route path="/spot-changes" element={<ManagerConsolePage />} />
                    {/* Manager Settings Page */}
                    <Route path="/manager/settings" element={<ManagerSettingsPage />} />
                    {/* Protected Admin Route */}
                    <Route path="/admin/stripe-payments" element={<ProtectedRoute requireAdmin><StripeAdminPage /></ProtectedRoute>} />
                    <Route
                      path="/admin/*"
                      element={<AdminDashboard />}
                    />
                    <Route
                      path="/admin/hiring"
                      element={<HiringOperationsPage />}
                    />
                    {/* Error Alerts List Page (Admin Only) */}
                    <Route
                      path="/admin/errors"
                      element={<ErrorAlertsPage />}
                    />
                    {/* Error Alert Details Page (Admin Only) */}
                    <Route
                      path="/admin/error/:errorHash"
                      element={<ErrorAlertPage />}
                    />
                    {/* Monthly Report Page (Admin Only) */}
                    <Route
                      path="/admin/report"
                      element={<ReportPage />}
                    />
                    {/* PM Partners Admin Page */}
                    <Route
                      path="/admin/partners"
                      element={<PartnersAdminPage />}
                    />
                    {/* Landlords Admin Page */}
                    <Route
                      path="/admin/landlords"
                      element={<LandlordsAdminPage />}
                    />
                    {/* Future Customer Routes */}
                    <Route
                      path="/dashboard/*"
                      element={
                        <ProtectedRoute requireCustomer={true}>
                          <div className="min-h-screen bg-[#6D6E71] flex items-center justify-center">
                            <div className="text-center">
                              <h1 className="text-2xl font-bold text-[#efeff1] mb-4">
                                Customer Dashboard
                              </h1>
                              <p className="text-[#b8b8b8]">
                                Coming soon - Customer features will be added here
                              </p>
                            </div>
                          </div>
                        </ProtectedRoute>
                      }
                    />
                    {/* Settings Route (renamed from Profile) */}
                    <Route
                      path="/settings"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={
                            <div className="min-h-screen bg-[#6D6E71] flex items-center justify-center">
                              <LoadingSpinner />
                            </div>
                          }>
                            <AppSettingsPage />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    {/* Legacy profile route redirect */}
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Suspense fallback={
                            <div className="min-h-screen bg-[#6D6E71] flex items-center justify-center">
                              <LoadingSpinner />
                            </div>
                          }>
                            <AppSettingsPage />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    />
                    <Route path="*" element={<div>Page Not Found</div>} />
                    </InstrumentedRoutes>
                  </Suspense>
                </main>
                {showPublicChrome && (
                  <>
                    <YardFooter />
                    <FloatingManageBooking />
                  </>
                )}
              </div>
            </BackgroundLayout>
          </MaintenanceWrapper>
        </ThemeProvider>
      </AppProvider>
    </>
  );

  // Auth0ProviderWithHistory must always be mounted so the session survives
  // SPA navigation between admin routes and public routes. Previously the
  // provider was conditionally rendered based on enableAuthRuntime, which
  // destroyed the Auth0 context (and the session) every time the user
  // navigated away from an auth-required route.
  return (
    <Suspense fallback={<LoadingSpinner message="Loading..." />}>
      <Auth0ProviderWithHistory>
        <AuthSiteSettingsProvider>
          <BookingSessionProvider>
            <TenantIdentityProvider>
              {appFrame}
            </TenantIdentityProvider>
          </BookingSessionProvider>
        </AuthSiteSettingsProvider>
      </Auth0ProviderWithHistory>
    </Suspense>
  );
};

const AppRouterFrame = () => (
  <>
    <RouteScrollManager />
    <RouteSeoPolicy />
    <RouteAnalyticsTracker />
    <AppContent />
  </>
);

let appBrowserRouter = null;

const getAppBrowserRouter = () => {
  if (typeof window === 'undefined') return null;
  if (!appBrowserRouter) {
    appBrowserRouter = createBrowserRouter(
      [{ path: '*', element: <AppRouterFrame /> }],
      { future: { v7_relativeSplatPath: true } },
    );
  }
  return appBrowserRouter;
};

const App = ({ useRouter = true }) => {
  const browserRouter = useRouter ? getAppBrowserRouter() : null;
  if (browserRouter) {
    return <RouterProvider router={browserRouter} fallbackElement={<LoadingSpinner message="Loading..." />} future={{ v7_startTransition: true }} />;
  }
  return <AppContent />;
};

export default App; 
